import { useEffect, useState, useRef } from "react";
import { useParams, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { getQueryFn, apiRequest } from "@/lib/queryClient";
import { useQuery } from "@tanstack/react-query";
import { Slider } from "@/components/ui/slider";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDefaultDeck } from "@/hooks/use-default-deck";
import { cardImageCache } from "@/services/card-image-cache";
import {
  PlayCircle,
  PauseCircle,
  SkipBack,
  SkipForward,
  Rewind,
  FastForward,
  UserIcon,
  ArrowLeft,
  Trophy,
  Swords,
  Film,
} from "lucide-react";
import { EnhancedHeader } from "@/components/ui/enhanced-header";

interface Player {
  id: number;
  username: string;
}

interface GamePlayStep {
  player: number | null;
  card: string;
  timestamp: number;
  isLeading: boolean;
}

interface ReplayState {
  currentStep: number;
  isPlaying: boolean;
  playbackSpeed: number;
  player1Cards: string[];
  player2Cards: string[];
  player1PlayedCards: string[];
  player2PlayedCards: string[];
  currentTurn: number | null;
  isLeader: number | null; // Can be number when a player is leading
}

export default function ReplayPage() {
  const { gameId, roundId } = useParams();
  const [_, setLocation] = useLocation();
  const [replayState, setReplayState] = useState<ReplayState>({
    currentStep: 0,
    isPlaying: false,
    playbackSpeed: 1,
    player1Cards: [],
    player2Cards: [],
    player1PlayedCards: [],
    player2PlayedCards: [],
    currentTurn: null,
    isLeader: null,
  });
  const playbackTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMobile = useIsMobile();
  const defaultDeckQuery = useDefaultDeck();
  const deckUrl =
    defaultDeckQuery.data?.deckUrl ||
    "https://teacherwilliam.sgp1.cdn.digitaloceanspaces.com/spar/deck/free/default";

  // Fetch replay data
  const { data, isLoading, error } = useQuery({
    queryKey: ["replay", gameId, roundId],
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/game-rounds/${gameId}/${roundId}/replay`,
      );
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to fetch replay data");
      }
      return res.json();
    },
  });

  // Preload images when data is available
  useEffect(() => {
    if (data?.initialState && deckUrl) {
      // Create an array of all unique cards in the game
      const allCards = Array.from(
        new Set([
          ...data.initialState.player1Deck,
          ...data.initialState.player2Deck,
          // Add the back card to ensure it's preloaded
          "back",
        ]),
      );

      // Use card image cache service to preload deck images
      cardImageCache.preloadDeckImages(deckUrl, allCards);

      console.log("Preloading cards from deck URL:", deckUrl);
    }
  }, [data, deckUrl]);

  // Handle automatic playback
  useEffect(() => {
    if (replayState.isPlaying && data?.gameplaySequence) {
      const sequence = data.gameplaySequence as GamePlayStep[];

      // Clear existing timer
      if (playbackTimerRef.current) {
        clearTimeout(playbackTimerRef.current);
      }

      // Stop if we're at the end
      if (replayState.currentStep >= sequence.length) {
        setReplayState((prev) => ({ ...prev, isPlaying: false }));
        return;
      }

      // Set timeout for next step based on playback speed
      const timer = setTimeout(() => {
        stepForward();
      }, 1000 / replayState.playbackSpeed);

      playbackTimerRef.current = timer;

      // Cleanup on unmount
      return () => {
        if (playbackTimerRef.current) {
          clearTimeout(playbackTimerRef.current);
        }
      };
    }
  }, [
    replayState.isPlaying,
    replayState.currentStep,
    replayState.playbackSpeed,
    data,
  ]);

  // Initialize state from data
  useEffect(() => {
    if (data?.initialState && !isLoading) {
      setReplayState((prev) => ({
        ...prev,
        player1Cards: [...data.initialState.player1Deck],
        player2Cards: [...data.initialState.player2Deck],
        player1PlayedCards: [],
        player2PlayedCards: [],
        currentTurn: data.initialState.player1Id,
        isLeader: null,
      }));
    }
  }, [data, isLoading]);

  // Handle stepping forward in the replay
  const stepForward = () => {
    if (!data?.gameplaySequence) return;

    const sequence = data.gameplaySequence as GamePlayStep[];
    const nextStep = replayState.currentStep;

    if (nextStep < sequence.length) {
      const step = sequence[nextStep];
      const updatedState: ReplayState = {
        ...replayState,
        currentStep: nextStep + 1,
      };

      // Update played cards and remaining cards based on who played
      if (step.player === data.initialState.player1Id) {
        // Player 1 played a card
        updatedState.player1Cards = updatedState.player1Cards.filter(
          (c) => c !== step.card,
        );
        updatedState.player1PlayedCards = [
          ...updatedState.player1PlayedCards,
          step.card,
        ];
        updatedState.currentTurn = data.initialState.player2Id;
      } else {
        // Player 2 played a card
        updatedState.player2Cards = updatedState.player2Cards.filter(
          (c) => c !== step.card,
        );
        updatedState.player2PlayedCards = [
          ...updatedState.player2PlayedCards,
          step.card,
        ];
        updatedState.currentTurn = data.initialState.player1Id;
      }

      // Update leader if this move captured lead
      if (step.isLeading) {
        updatedState.isLeader = step.player;
      }

      setReplayState(updatedState);
    } else if (replayState.isPlaying) {
      // Stop playing if we reached the end
      setReplayState((prev) => ({ ...prev, isPlaying: false }));
    }
  };

  // Handle stepping backward in the replay
  const stepBackward = () => {
    if (!data?.gameplaySequence || replayState.currentStep <= 0) return;

    const sequence = data.gameplaySequence as GamePlayStep[];
    const prevStepIndex = replayState.currentStep - 1;
    const prevStep = sequence[prevStepIndex];

    const updatedState: ReplayState = {
      ...replayState,
      currentStep: prevStepIndex,
    };

    // Restore cards based on who played last
    if (prevStep.player === data.initialState.player1Id) {
      // Restore Player 1's card
      updatedState.player1Cards = [...updatedState.player1Cards, prevStep.card];
      updatedState.player1PlayedCards = updatedState.player1PlayedCards.slice(
        0,
        -1,
      );
      updatedState.currentTurn = data.initialState.player1Id;
    } else {
      // Restore Player 2's card
      updatedState.player2Cards = [...updatedState.player2Cards, prevStep.card];
      updatedState.player2PlayedCards = updatedState.player2PlayedCards.slice(
        0,
        -1,
      );
      updatedState.currentTurn = data.initialState.player2Id;
    }

    // Update leader if necessary (check previous steps to determine leader)
    let newLeader = null;
    for (let i = 0; i < prevStepIndex; i++) {
      if (sequence[i].isLeading) {
        newLeader = sequence[i].player;
      }
    }
    updatedState.isLeader = newLeader;

    setReplayState(updatedState);
  };

  // Restart the replay
  const restart = () => {
    if (!data?.initialState) return;

    setReplayState({
      currentStep: 0,
      isPlaying: false,
      playbackSpeed: replayState.playbackSpeed,
      player1Cards: [...data.initialState.player1Deck],
      player2Cards: [...data.initialState.player2Deck],
      player1PlayedCards: [],
      player2PlayedCards: [],
      currentTurn: data.initialState.player1Id,
      isLeader: null,
    });
  };

  // Toggle play/pause
  const togglePlayback = () => {
    setReplayState((prev) => ({ ...prev, isPlaying: !prev.isPlaying }));
  };

  // Set playback speed
  const setPlaybackSpeed = (speed: number) => {
    setReplayState((prev) => ({ ...prev, playbackSpeed: speed }));
  };

  // Skip to end
  const skipToEnd = () => {
    if (!data?.gameplaySequence) return;

    const sequence = data.gameplaySequence as GamePlayStep[];
    let updatedState: ReplayState = {
      ...replayState,
      currentStep: sequence.length,
      isPlaying: false,
      player1Cards: [] as string[],
      player2Cards: [] as string[],
      player1PlayedCards: [] as string[],
      player2PlayedCards: [] as string[],
      currentTurn: null,
      isLeader: null,
    };

    // Apply all moves
    sequence.forEach((step) => {
      if (step.player === data.initialState.player1Id) {
        updatedState.player1PlayedCards = [
          ...updatedState.player1PlayedCards,
          step.card,
        ];
      } else {
        updatedState.player2PlayedCards = [
          ...updatedState.player2PlayedCards,
          step.card,
        ];
      }

      if (step.isLeading) {
        updatedState.isLeader = step.player;
      }
    });

    setReplayState(updatedState);
  };

  // Render card component
  const renderCard = (card: string, isPlayed: boolean = false) => {
    if (!deckUrl) {
      return (
        <div
          key={card}
          className={`relative rounded-md overflow-hidden border border-white/10 bg-black/30 shadow-lg hover:shadow-primary/20 flex items-center justify-center 
            ${isPlayed ? "transform transition-all duration-300 hover:scale-105" : "transition-all duration-300 hover:scale-105"}`}
          style={{
            width: isMobile ? "50px" : "70px",
            height: isMobile ? "75px" : "105px",
          }}
        >
          <p className="text-xs text-white/60">{card}</p>
        </div>
      );
    }

    return (
      <div
        key={card}
        className={`relative rounded-md overflow-hidden border border-white/10 bg-black/30 shadow-lg hover:shadow-primary/20 
          ${isPlayed ? "transform transition-all duration-300 hover:scale-105" : "transition-all duration-300 hover:scale-105"}`}
        style={{
          width: isMobile ? "50px" : "70px",
          height: isMobile ? "75px" : "105px",
        }}
      >
        <img
          src={`${deckUrl}/${card}.svg`}
          alt={card}
          className="w-full h-full object-cover"
          onError={(e) => {
            // Fallback if image fails to load
            (e.target as HTMLImageElement).src = `${deckUrl}/back.svg`;
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/40"></div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="container max-w-4xl mx-auto px-4 min-h-screen flex flex-col">
        <EnhancedHeader
          title="Game Replay"
          backPath={`/game-history/${gameId}`}
          accentColor="text-primary"
        />

        <div className="flex flex-col flex-grow items-center justify-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <h2 className="text-xl font-semibold text-white">
            Loading Replay Data
          </h2>
          <p className="text-white/60 text-center">
            Loading the gameplay sequence...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container max-w-4xl mx-auto px-4 min-h-screen flex flex-col">
        <EnhancedHeader
          title="Game Replay"
          backPath={`/game-history/${gameId}`}
          accentColor="text-primary"
        />

        <div className="flex flex-col flex-grow items-center justify-center gap-4 max-w-md mx-auto">
          <div className="rounded-full bg-red-500/10 p-3">
            <Swords className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-white">
            Error Loading Replay
          </h2>
          <p className="text-white/60 text-center">
            {(error as Error).message ||
              "Failed to load the replay data. Please try again later."}
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container max-w-4xl mx-auto px-4 min-h-screen flex flex-col">
        <EnhancedHeader
          title="Game Replay"
          backPath={`/game-history/${gameId}`}
          accentColor="text-primary"
        />

        <div className="flex flex-col flex-grow items-center justify-center gap-4 max-w-md mx-auto">
          <div className="rounded-full bg-yellow-500/10 p-3">
            <Swords className="h-8 w-8 text-yellow-500" />
          </div>
          <h2 className="text-xl font-semibold text-white">No Replay Data</h2>
          <p className="text-white/60 text-center">
            No replay information was found for this game round.
          </p>
        </div>
      </div>
    );
  }

  const { initialState, roundInfo } = data;
  const sequence = data.gameplaySequence as GamePlayStep[];
  const progress =
    sequence.length > 0 ? (replayState.currentStep / sequence.length) * 100 : 0;

  // Create Playback Controls component for the header right content
  const PlaybackControls = () => (
    <Button
      variant="ghost"
      size="icon"
      onClick={togglePlayback}
      className="rounded-full bg-black/80 hover:bg-black/60 text-white w-10 h-10"
    >
      {replayState.isPlaying ? (
        <PauseCircle className="h-5 w-5" />
      ) : (
        <PlayCircle className="h-5 w-5" />
      )}
    </Button>
  );

  return (
    <div className="container max-w-4xl mx-auto px-4 pb-20">
      <EnhancedHeader
        title="Game Replay"
        backPath={`/game-history/${gameId}`}
        accentColor="text-primary"
        rightContent={<PlaybackControls />}
      />

      {/* Game info */}
      <div className="mb-8 grid grid-cols-2 gap-6 mt-4">
        <Card className="overflow-hidden backdrop-blur-sm border border-white/5 bg-gradient-to-r from-black/60 to-black/40">
          <div className="px-4 py-4 border-b border-white/5 bg-black/30">
            <h2 className="text-md font-medium flex items-center">
              <UserIcon className="h-4 w-4 mr-2 text-primary" />
              {initialState.player1Username}
            </h2>
          </div>
          <div className="p-4">
            <p className="text-md font-medium">
              Score:{" "}
              <span className="text-md font-bold">
                {roundInfo.player1Score}
              </span>
            </p>
            {roundInfo.winner === initialState.player1Id && (
              <div className="mt-2 px-3 py-1 inline-flex items-center justify-center rounded-md bg-primary/20 text-primary border border-primary/20 font-medium">
                <Trophy className="h-4 w-4 mr-1" /> Winner!
              </div>
            )}
          </div>
        </Card>

        <Card className="overflow-hidden backdrop-blur-sm border border-white/5 bg-gradient-to-r from-black/60 to-black/40">
          <div className="px-4 py-4 border-b border-white/5 bg-black/30">
            <h2 className="text-md font-medium flex items-center">
              <UserIcon className="h-4 w-4 mr-2 text-primary" />
              {initialState.player2Username}
            </h2>
          </div>
          <div className="p-4">
            <p className="text-md font-medium">
              Score:{" "}
              <span className="text-md font-bold">
                {roundInfo.player2Score}
              </span>
            </p>
            {roundInfo.winner === initialState.player2Id && (
              <div className="text-sm mt-2 px-3 py-1 inline-flex items-center justify-center rounded-md bg-primary/20 text-primary border border-primary/20 font-medium">
                <Trophy className="h-4 w-4 mr-1" /> Winner!
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Game status bar */}
      <Card className="mb-6 overflow-hidden backdrop-blur-sm border border-white/5 bg-gradient-to-r from-black/70 to-black/50">
        <div className="p-4">
          <div className="grid grid-cols-3 gap-2">
            <div className="flex flex-col items-center justify-center">
              <p className="text-sm text-white/60 mb-1">Current Turn</p>
              <div className="py-1 px-3 rounded-full bg-primary/10 text-primary border border-primary/20">
                {replayState.currentTurn === initialState.player1Id
                  ? initialState.player1Username
                  : replayState.currentTurn === initialState.player2Id
                    ? initialState.player2Username
                    : "None"}
              </div>
            </div>

            <div className="flex flex-col items-center justify-center">
              <p className="text-sm text-white/60 mb-1">Step</p>
              <div className="py-1 px-3 rounded-full bg-white/10 text-white border border-white/10">
                {replayState.currentStep} / {sequence.length}
              </div>
            </div>

            <div className="flex flex-col items-center justify-center">
              <p className="text-sm text-white/60 mb-1">Leading</p>
              <div className="py-1 px-3 rounded-full bg-primary/10 text-primary border border-primary/20">
                {replayState.isLeader === initialState.player1Id
                  ? initialState.player1Username
                  : replayState.isLeader === initialState.player2Id
                    ? initialState.player2Username
                    : "None"}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Gameplay area */}
      <div className="game-replay-container space-y-6 mb-8">
        {/* Player 2 section */}
        <div className="player2-section bg-black/20 rounded-lg p-4 border border-white/5">
          <h3 className="text-md font-semibold mb-3 flex items-center gap-2">
            <UserIcon className="h-4 w-4 text-primary" />
            <span>{initialState.player2Username}'s Area</span>
          </h3>

          <div className="space-y-4">
            <div>
              <h4 className="text-sm text-white/60 mb-2 border-b border-white/10 pb-1">
                Hand
              </h4>
              <div className="flex flex-wrap gap-3 min-h-[100px] items-center">
                {replayState.player2Cards.length > 0 ? (
                  replayState.player2Cards.map((card) => renderCard(card))
                ) : (
                  <p className="text-sm text-white/40">No cards in hand</p>
                )}
              </div>
            </div>

            <div>
              <h4 className="text-sm text-white/60 mb-2 border-b border-white/10 pb-1">
                Played Cards
              </h4>
              <div className="flex flex-wrap gap-3 min-h-[100px] items-center">
                {replayState.player2PlayedCards.length > 0 ? (
                  replayState.player2PlayedCards.map((card) =>
                    renderCard(card, true),
                  )
                ) : (
                  <p className="text-sm text-white/40">No cards played</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Player 1 section */}
        <div className="player1-section bg-black/20 rounded-lg p-4 border border-white/5">
          <div className="space-y-4">
            <div>
              <div className="flex flex-wrap gap-3 min-h-[100px] items-center">
                {replayState.player1PlayedCards.length > 0 ? (
                  replayState.player1PlayedCards.map((card) =>
                    renderCard(card, true),
                  )
                ) : (
                  <p className="text-sm text-white/40">No cards played</p>
                )}
              </div>
              <h4 className="text-sm text-white/60 mb-2 border-b border-white/10 pb-1">
                Played Cards
              </h4>
            </div>
            <h3 className="text-md font-semibold mb-3 flex items-center gap-2">
              <UserIcon className="h-4 w-4 text-primary" />
              <span>{initialState.player1Username}'s Area</span>
            </h3>

            <div>
              <h4 className="text-sm text-white/60 mb-2 border-b border-white/10 pb-1">
                Hand
              </h4>
              <div className="flex flex-wrap gap-3 min-h-[100px] items-center">
                {replayState.player1Cards.length > 0 ? (
                  replayState.player1Cards.map((card) => renderCard(card))
                ) : (
                  <p className="text-sm text-white/40">No cards in hand</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Playback controls */}
      <Card className="overflow-hidden backdrop-blur-sm border border-white/5 bg-gradient-to-r from-black/60 to-black/40">
        <div className="px-4 py-4 border-b border-white/5 bg-black/30">
          <h3 className="text-lg font-medium flex items-center">
            <PlayCircle className="h-4 w-4 mr-2 text-primary" />
            Playback Controls
          </h3>
        </div>

        <div className="p-4 space-y-6">
          {/* Playback buttons */}
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              size="icon"
              onClick={restart}
              title="Restart"
              className="bg-primary/10 border-primary/20 text-primary hover:bg-primary/20"
            >
              <SkipBack className="h-4 w-4" />
            </Button>

            <Button
              variant="outline"
              size="icon"
              onClick={stepBackward}
              disabled={replayState.currentStep <= 0}
              title="Step backward"
              className="bg-primary/10 border-primary/20 text-primary hover:bg-primary/20 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Rewind className="h-4 w-4" />
            </Button>

            <Button
              variant={replayState.isPlaying ? "default" : "outline"}
              size="icon"
              onClick={togglePlayback}
              title={replayState.isPlaying ? "Pause" : "Play"}
              className={
                replayState.isPlaying
                  ? "bg-primary text-white hover:bg-primary/90"
                  : "bg-primary/10 border-primary/20 text-primary hover:bg-primary/20"
              }
            >
              {replayState.isPlaying ? (
                <PauseCircle className="h-6 w-6" />
              ) : (
                <PlayCircle className="h-6 w-6" />
              )}
            </Button>

            <Button
              variant="outline"
              size="icon"
              onClick={stepForward}
              disabled={replayState.currentStep >= sequence.length}
              title="Step forward"
              className="bg-primary/10 border-primary/20 text-primary hover:bg-primary/20 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <FastForward className="h-4 w-4" />
            </Button>

            <Button
              variant="outline"
              size="icon"
              onClick={skipToEnd}
              title="Skip to end"
              className="bg-primary/10 border-primary/20 text-primary hover:bg-primary/20"
            >
              <SkipForward className="h-4 w-4" />
            </Button>
          </div>

          {/* Progress bar */}
          <div>
            <Slider
              value={[progress]}
              max={100}
              step={1}
              className="w-full"
              onValueChange={(value) => {
                if (!data?.gameplaySequence) return;
                const newStep = Math.floor((value[0] / 100) * sequence.length);

                // Reset to initial state then apply all steps up to the new position
                restart();

                // We need to apply steps one by one
                setTimeout(() => {
                  let updatedState: ReplayState = {
                    ...replayState,
                    currentStep: newStep,
                    player1Cards: [...data.initialState.player1Deck],
                    player2Cards: [...data.initialState.player2Deck],
                    player1PlayedCards: [] as string[],
                    player2PlayedCards: [] as string[],
                    isLeader: null,
                  };

                  // Apply all steps up to new position
                  for (let i = 0; i < newStep; i++) {
                    const step = sequence[i];
                    if (step.player === data.initialState.player1Id) {
                      updatedState.player1Cards =
                        updatedState.player1Cards.filter(
                          (c) => c !== step.card,
                        );
                      updatedState.player1PlayedCards = [
                        ...updatedState.player1PlayedCards,
                        step.card,
                      ];
                    } else {
                      updatedState.player2Cards =
                        updatedState.player2Cards.filter(
                          (c) => c !== step.card,
                        );
                      updatedState.player2PlayedCards = [
                        ...updatedState.player2PlayedCards,
                        step.card,
                      ];
                    }

                    if (step.isLeading) {
                      updatedState.isLeader = step.player;
                    }
                  }

                  // Set current turn based on last step
                  if (newStep > 0 && newStep <= sequence.length) {
                    const lastStep = sequence[newStep - 1];
                    updatedState.currentTurn =
                      lastStep.player === data.initialState.player1Id
                        ? data.initialState.player2Id
                        : data.initialState.player1Id;
                  }

                  setReplayState(updatedState);
                }, 0);
              }}
            />
          </div>

          {/* Playback speed controls */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-2">
            <span className="text-sm text-white/60">Playback Speed:</span>
            <div className="flex gap-2">
              <Button
                variant={
                  replayState.playbackSpeed === 0.5 ? "default" : "outline"
                }
                size="sm"
                onClick={() => setPlaybackSpeed(0.5)}
                className={`px-2 py-1 h-8 ${
                  replayState.playbackSpeed === 0.5
                    ? "bg-primary text-white hover:bg-primary/90"
                    : "bg-primary/10 border-primary/20 text-primary hover:bg-primary/20"
                }`}
              >
                0.5x
              </Button>
              <Button
                variant={
                  replayState.playbackSpeed === 1 ? "default" : "outline"
                }
                size="sm"
                onClick={() => setPlaybackSpeed(1)}
                className={`px-2 py-1 h-8 ${
                  replayState.playbackSpeed === 1
                    ? "bg-primary text-white hover:bg-primary/90"
                    : "bg-primary/10 border-primary/20 text-primary hover:bg-primary/20"
                }`}
              >
                1x
              </Button>
              <Button
                variant={
                  replayState.playbackSpeed === 1.5 ? "default" : "outline"
                }
                size="sm"
                onClick={() => setPlaybackSpeed(1.5)}
                className={`px-2 py-1 h-8 ${
                  replayState.playbackSpeed === 1.5
                    ? "bg-primary text-white hover:bg-primary/90"
                    : "bg-primary/10 border-primary/20 text-primary hover:bg-primary/20"
                }`}
              >
                1.5x
              </Button>
              <Button
                variant={
                  replayState.playbackSpeed === 2 ? "default" : "outline"
                }
                size="sm"
                onClick={() => setPlaybackSpeed(2)}
                className={`px-2 py-1 h-8 ${
                  replayState.playbackSpeed === 2
                    ? "bg-primary text-white hover:bg-primary/90"
                    : "bg-primary/10 border-primary/20 text-primary hover:bg-primary/20"
                }`}
              >
                2x
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
